import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link } from 'react-router-dom';

import type {
  Connection,
  ConnectionSummary,
  ConnectorProfile,
} from '@/shared/api/generated/catalog';

import { CONNECTOR_LABEL, addressOf } from '@/entities/connection';
import { BrandLoader } from '@/shared/ui/BrandLoader';

import { useRequestHarvest, useVerifyConnection } from '../api/connections';
import { capabilitiesOf } from '../model/capabilities';
import type { Checked } from '../model/listing';
import { ageSince, harvestReading, lastChecked } from '../model/listing';
import { versionReading, versionSentence, versionText } from '../model/version';

/* The sources, as the design's card grid.
 *
 * **Cards again, ordered so the ones that need a person come first.** The previous pass made this a
 * table on the argument that "which of these needs me" is a scan down one column. The redesign keeps
 * that ordering — `byAttention` still decides it, so a failed source is the first card — and spends the
 * card's room on what a table had to push onto the detail page: the reach a source has, its three
 * facts, a live harvest with its steps, and the sentence that says what to do next.
 *
 * **Verify and Harvest are on the card, and both are the mutations the source's own page uses.** A
 * verification answers 200 whether or not the source replied, so the card reports `reachable` from
 * the response rather than treating "unreachable" as an error — the same reading the detail page makes.
 * Harvest is offered only for a verified source with no harvest running: every path that starts one
 * refuses anything else, and a button the server refuses is a button that teaches a reader to distrust
 * the others.
 *
 * **The state pill's dot is hollow for the two states nobody has measured** — never checked, and out
 * of service — so "we do not know" reads differently from a state somebody tested, in greyscale too.
 */

type Translate = TFunction;

const STATE_TONE = {
  verified: 'ok',
  failed: 'error',
  registered: 'unknown',
  disabled: 'unknown',
} as const satisfies Record<Connection['state'], string>;

/** Whether the pill's dot is drawn hollow: the states that report no measurement. */
const HOLLOW: Readonly<Record<Connection['state'], boolean>> = {
  verified: false,
  failed: false,
  registered: true,
  disabled: true,
};

function agoLabel(t: Translate, checked: Checked): string {
  switch (checked.kind) {
    case 'never':
      return t('connections.checked.never');
    case 'justNow':
      return t('connections.checked.justNow');
    case 'minutes':
      return t('connections.checked.minutes', { count: checked.count });
    case 'hours':
      return t('connections.checked.hours', { count: checked.count });
    default:
      return t('connections.checked.days', { count: checked.count });
  }
}

/** The harvest's four steps, and which one a run is on — derived from what the summary reports. */
const STEPS = ['connect', 'discover', 'read', 'record'] as const;

interface VerifyNote {
  readonly reachable: boolean;
  readonly detail: string | null;
}

function ConnectionCard({
  connection,
  summary,
  profiles,
  now,
}: {
  readonly connection: Connection;
  readonly summary: ConnectionSummary | undefined;
  readonly profiles: readonly ConnectorProfile[] | undefined;
  readonly now: Date;
}): ReactElement {
  const { t } = useTranslation();
  const verify = useVerifyConnection();
  const harvest = useRequestHarvest();
  const client = useQueryClient();
  const [note, setNote] = useState<VerifyNote | null>(null);
  const [harvestError, setHarvestError] = useState<string | null>(null);

  const reading = harvestReading(summary);
  const running =
    reading.kind === 'running' || reading.kind === 'starting' || harvest.isPending;
  const stepAt = reading.kind === 'running' ? 2 : reading.kind === 'starting' ? 1 : 0;
  const percent =
    reading.kind === 'running' && reading.discovered > 0
      ? Math.floor((reading.observed / reading.discovered) * 100)
      : 0;

  const caps = profiles === undefined ? null : capabilitiesOf(connection, profiles);
  const reach =
    caps === null
      ? null
      : caps.rules === 'yes' && caps.profiling === 'yes'
        ? 'full'
        : /* Rules without profiling — a version too old to profile, or a deployment with no
             sketch source. Not "metadata only": rules still run, and saying otherwise would
             understate the source. */
          caps.rules === 'yes'
          ? 'rules'
          : caps.harvest === 'yes'
            ? 'metadata'
            : 'none';

  /* What to do next, in one sentence: the last verification's answer if somebody just asked, then a
     refused harvest, then what the source's own state calls for. */
  const stateMessage =
    connection.state === 'failed'
      ? t('connections.card.failedMessage', { secret: connection.secret_ref })
      : connection.state === 'registered'
        ? t('connections.card.registeredMessage')
        : connection.state === 'disabled'
          ? t('connections.card.disabledMessage')
          : reading.kind === 'failed'
            ? t('connections.card.harvestFailedMessage')
            : reading.kind === 'partial'
              ? t('connections.card.harvestPartialMessage')
              : null;
  const verifyMessage =
    note === null
      ? null
      : note.reachable
        ? t('connections.card.reachable')
        : t('connections.card.unreachable', { detail: note.detail ?? '' });
  const message = verifyMessage ?? harvestError ?? stateMessage;

  const datasets = summary === undefined ? null : summary.datasets;
  const scored =
    summary === undefined
      ? null
      : summary.scored === null
        ? t('connections.card.notConsulted')
        : t('connections.card.scored', { count: summary.scored });

  return (
    <article className="lq-cn__card" data-state={connection.state}>
      <div className="lq-cn__head">
        <span className="lq-cn__glyph" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          >
            <ellipse cx="12" cy="5.5" rx="7.5" ry="2.8" />
            <path d="M4.5 5.5v6c0 1.55 3.36 2.8 7.5 2.8s7.5-1.25 7.5-2.8v-6" />
            <path d="M4.5 11.5v6c0 1.55 3.36 2.8 7.5 2.8s7.5-1.25 7.5-2.8v-6" />
          </svg>
        </span>
        <div className="lq-cn__who">
          <div className="lq-cn__nameRow">
            <Link className="lq-cn__name" to={`/connections/${connection.id}`}>
              {connection.name}
            </Link>
            <span className="lq-cn__type">
              {CONNECTOR_LABEL[connection.connector_type]}
            </span>
            <VersionChip connection={connection} />
          </div>
          <div className="lq-cn__address" title={addressOf(connection)}>
            {addressOf(connection)}
          </div>
        </div>
        <span
          className="lq-cn__state"
          data-tone={STATE_TONE[connection.state]}
          title={t(`connections.state.${connection.state}.meaning`)}
        >
          <span
            className={`lq-cn__dot${HOLLOW[connection.state] ? ' lq-cn__dot--hollow' : ''}`}
            aria-hidden="true"
          />
          {t(`connections.card.state.${connection.state}`)}
        </span>
      </div>

      {reach === null ? null : (
        <span className="lq-cn__reach" data-reach={reach}>
          {t(`connections.card.reach.${reach}`)}
        </span>
      )}

      <div className="lq-cn__facts">
        <div>
          <div className="lq-cn__fact">
            {datasets === null ? '—' : datasets.toLocaleString()}
          </div>
          <div className="lq-cn__factLabel">
            {scored === null
              ? t('connections.card.datasetsOnly')
              : t('connections.card.datasets', { scored })}
          </div>
        </div>
        <div>
          <div className="lq-cn__fact lq-cn__fact--small">
            {agoLabel(t, ageSince(summary?.last_harvest_at ?? null, now))}
          </div>
          <div className="lq-cn__factLabel">{t('connections.card.lastHarvest')}</div>
        </div>
        <div>
          <div className="lq-cn__fact lq-cn__fact--small">
            {agoLabel(t, lastChecked(connection, now))}
          </div>
          <div className="lq-cn__factLabel">{t('connections.card.lastVerified')}</div>
        </div>
      </div>

      {running ? (
        <div className="lq-cn__harvest" role="status">
          <div className="lq-cn__harvestHead">
            <BrandLoader size="sm" />
            <span className="lq-cn__harvestTitle">
              {t('connections.card.harvesting')}
            </span>
            <span className="lq-cn__harvestCount">
              {reading.kind === 'running'
                ? t('connections.card.objectsRead', {
                    observed: reading.observed,
                    discovered: reading.discovered,
                  })
                : t('connections.card.discovering')}
            </span>
          </div>
          <div className="lq-cn__track">
            {reading.kind === 'running' ? (
              <div
                className="lq-cn__bar"
                style={{ width: `${String(percent)}%` }}
                data-grow=""
              />
            ) : (
              <div className="lq-cn__bar lq-cn__bar--hatched" />
            )}
          </div>
          <ol className="lq-cn__steps">
            {STEPS.map((step, index) => (
              <li
                key={step}
                className="lq-cn__step"
                data-at={index < stepAt ? 'done' : index === stepAt ? 'now' : 'next'}
              >
                <span className="lq-cn__stepMark" aria-hidden="true">
                  {index < stepAt ? '✓' : index === stepAt ? '●' : '○'}
                </span>
                {t(`connections.card.step.${step}`)}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {message === null ? null : (
        <p
          className="lq-cn__message"
          data-tone={note?.reachable === false ? 'error' : undefined}
        >
          {message}
        </p>
      )}

      <div className="lq-cn__actions">
        <button
          type="button"
          className="lq-cn__btn"
          disabled={verify.isPending}
          onClick={() => {
            setNote(null);
            verify.mutate(connection.id, {
              onSuccess: (result) => {
                setNote({ reachable: result.reachable, detail: result.detail });
              },
              onError: (error) => {
                setNote({ reachable: false, detail: error.message });
              },
            });
          }}
        >
          {verify.isPending ? t('connections.card.verifying') : t('connections.verify')}
        </button>
        {connection.state === 'verified' && !running ? (
          <button
            type="button"
            className="lq-cn__btn"
            disabled={harvest.isPending}
            onClick={() => {
              setHarvestError(null);
              harvest.mutate(connection.id, {
                /* The card's progress comes from the summaries, so they are re-read the moment a
                   harvest is accepted rather than at the next poll. */
                onSuccess: () => {
                  void client.invalidateQueries({
                    queryKey: ['catalog', 'connection-summaries'],
                  });
                },
                onError: (error) => {
                  setHarvestError(error.message);
                },
              });
            }}
          >
            {t('connections.harvest')}
          </button>
        ) : null}
        <span className="lq-cn__spacer" />
        <Link className="lq-cn__open" to={`/connections/${connection.id}`}>
          {t('connections.card.open')}
        </Link>
      </div>
    </article>
  );
}

export function ConnectionCards({
  connections,
  summaries,
  profiles,
  now,
}: {
  readonly connections: readonly Connection[];
  /** Harvest progress per source, keyed by id. Absent for a summary read that failed or is pending. */
  readonly summaries: ReadonlyMap<string, ConnectionSummary>;
  /** The connector catalogue, for each card's reach. `undefined` while it loads or if it failed. */
  readonly profiles: readonly ConnectorProfile[] | undefined;
  readonly now: Date;
}): ReactElement {
  return (
    <div className="lq-cn__grid">
      {connections.map((connection) => (
        <ConnectionCard
          key={connection.id}
          connection={connection}
          summary={summaries.get(connection.id)}
          profiles={profiles}
          now={now}
        />
      ))}
    </div>
  );
}

/* The version a source reported, beside its type. Nothing is drawn for the two absences — a source
   not read yet, or one that reported nothing — because a card has no room to say which, and the
   detail page does. A limited version is amber and says so in words as well as colour. */
function VersionChip({
  connection,
}: {
  readonly connection: Connection;
}): ReactElement | null {
  const { t } = useTranslation();
  const reading = versionReading(connection.source_version);
  const text = versionText(reading);
  if (text === null) return null;
  const product = CONNECTOR_LABEL[connection.connector_type];
  return (
    <span
      className="lq-cn__version"
      data-support={reading.kind}
      title={t('connections.version.chipTitle', {
        product,
        version: text,
        sentence: versionSentence(t, reading, product),
      })}
    >
      {reading.kind === 'limited'
        ? t('connections.version.chipLimited', { version: text })
        : text}
    </span>
  );
}
